import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PhotoData, PDFOptions } from '../types/photo';

export class PDFService {
  private static instance: PDFService;
  
  private constructor() {}
  
  public static getInstance(): PDFService {
    if (!PDFService.instance) {
      PDFService.instance = new PDFService();
    }
    return PDFService.instance;
  }

  /**
   * Convert image data URL to PDF with OCR-optimized quality
   */
  public async convertImageToPDF(
    photoData: PhotoData, 
    options: PDFOptions = this.getDefaultOptions()
  ): Promise<string> {
    try {
      // Create a temporary canvas element
      const canvas = await this.createCanvasFromImage(photoData.dataUrl);
      
      // Calculate PDF dimensions
      const pdfDimensions = this.calculatePDFDimensions(canvas, options);
      
      // Create PDF document with high quality settings
      const pdf = new jsPDF({
        orientation: options.orientation,
        unit: 'mm',
        format: options.format,
        compress: false, // Disable compression for better quality
        precision: 16 // High precision for better image quality
      });

      // Add image to PDF with high quality
      const imgData = canvas.toDataURL('image/jpeg', options.imageQuality);
      pdf.addImage(imgData, 'JPEG', options.margin, options.margin, 
        pdfDimensions.width, pdfDimensions.height, '', 'FAST', 0);

      // Add metadata if requested
      if (options.includeMetadata) {
        this.addMetadata(pdf, photoData);
      }

      // Convert to base64 with high quality
      const pdfBase64 = pdf.output('datauristring');
      return pdfBase64.split(',')[1]; // Remove data:application/pdf;base64, prefix
      
    } catch (error) {
      console.error('PDF conversion error:', error);
      throw new Error('Failed to convert image to PDF');
    }
  }

  /**
   * Create canvas from image data URL with enhanced quality
   */
  private async createCanvasFromImage(dataUrl: string): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Set canvas dimensions
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Enable high-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw image to canvas
        ctx.drawImage(img, 0, 0);
        resolve(canvas);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  }

  /**
   * Calculate PDF dimensions maintaining aspect ratio with OCR optimization
   */
  private calculatePDFDimensions(canvas: HTMLCanvasElement, options: PDFOptions) {
    const pageWidth = this.getPageWidth(options.format, options.orientation);
    const pageHeight = this.getPageHeight(options.format, options.orientation);
    
    const availableWidth = pageWidth - (options.margin * 2);
    const availableHeight = pageHeight - (options.margin * 2);
    
    const aspectRatio = canvas.width / canvas.height;
    
    let width = availableWidth;
    let height = width / aspectRatio;
    
    // If height exceeds available space, scale down
    if (height > availableHeight) {
      height = availableHeight;
      width = height * aspectRatio;
    }
    
    // Ensure minimum dimensions for OCR quality
    const minWidth = 150; // Minimum 150mm width for OCR
    const minHeight = 100; // Minimum 100mm height for OCR
    
    if (width < minWidth) {
      width = minWidth;
      height = width / aspectRatio;
    }
    
    if (height < minHeight) {
      height = minHeight;
      width = height * aspectRatio;
    }
    
    return { width, height };
  }

  /**
   * Get page width in mm
   */
  private getPageWidth(format: string, orientation: string): number {
    const dimensions = this.getPageDimensions(format);
    return orientation === 'landscape' ? dimensions.height : dimensions.width;
  }

  /**
   * Get page height in mm
   */
  private getPageHeight(format: string, orientation: string): number {
    const dimensions = this.getPageDimensions(format);
    return orientation === 'landscape' ? dimensions.width : dimensions.height;
  }

  /**
   * Get page dimensions for different formats
   */
  private getPageDimensions(format: string): { width: number; height: number } {
    switch (format) {
      case 'a4':
        return { width: 210, height: 297 };
      case 'letter':
        return { width: 215.9, height: 279.4 };
      case 'legal':
        return { width: 215.9, height: 355.6 };
      default:
        return { width: 210, height: 297 }; // A4 default
    }
  }

  /**
   * Add metadata to PDF
   */
  private addMetadata(pdf: jsPDF, photoData: PhotoData) {
    const metadata = [
      `File: ${photoData.fileName}`,
      `Captured: ${photoData.timestamp}`,
      `Dimensions: ${photoData.width} x ${photoData.height}`,
      `Size: ${(photoData.fileSize / 1024).toFixed(2)} KB`,
      `Quality: OCR-Optimized`
    ];
    
    const yPosition = this.getPageHeight('a4', 'portrait') - 20;
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    
    metadata.forEach((text, index) => {
      pdf.text(text, 10, yPosition - (index * 5));
    });
  }

  /**
   * Get default PDF options optimized for OCR
   */
  private getDefaultOptions(): PDFOptions {
    return {
      orientation: 'portrait',
      format: 'a4',
      margin: 10,
      includeMetadata: true,
      dpi: 300, // High DPI for better OCR
      imageQuality: 0.95, // High image quality for OCR
      compression: 'jpeg' // JPEG for good quality/size balance
    };
  }
}

export default PDFService.getInstance();
